import React, { useState, useRef } from 'react';
import './Combobox.scss';
import useClickOutside from '../../utils/useClickOutside';

// --- SVG Icons ---
const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    className="spinner"
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const XIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

// --- The Component ---
const Combobox = ({
  options = [],
  value,
  label,
  disabled = false,
  onChange,
  onSearch,
  onClear = () => {},
  loading = false,
  labelKey = 'label',
  valueKey = 'id',
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',

  renderSelected = null, // (option) => string | JSX - How to display selected item
  renderOption = null,

  // Customization Props
  theme = 'light', // 'carbon', 'light', 'dark', or 'custom'
  size = 'lg', // 'sm', 'md', 'lg'

  // CSS Custom Properties Override
  customStyles = {},

  // Component-specific className overrides
  classNames = {
    root: '',
    trigger: '',
    label: '',
    actions: '',
    clear: '',
    chevron: '',
    menu: '',
    search: '',
    searchInput: '',
    list: '',
    item: '',
    emptyItem: '',
  },

  // Individual style overrides
  styles = {
    root: {},
    trigger: {},
    label: {},
    actions: {},
    clear: {},
    menu: {},
    search: {},
    searchInput: {},
    list: {},
    item: {},
    emptyItem: {},
  },
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const comboboxRef = useRef(null);

  useClickOutside(comboboxRef, () => setIsOpen(false), { closeOnEscape: true });

  const handleSelectOption = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    onSearch(term);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onClear();
  };

  // Get display text for selected value
  const getSelectedLabel = () => {
    if (!value) return placeholder;
    if (renderSelected) return renderSelected(value);
    return value[labelKey];
  };

  // Get display text for option in list
  const getOptionLabel = (option) => {
    if (renderOption) return renderOption(option);
    return option[labelKey];
  };

  const selectedLabel = getSelectedLabel();

  // Generate CSS custom properties from customStyles prop
  const cssCustomProperties = Object.entries(customStyles).reduce(
    (acc, [key, value]) => {
      acc[`--combobox-${key}`] = value;
      return acc;
    },
    {},
  );

  return (
    <div
      className={`combobox combobox--${theme} combobox--${size} ${classNames.root}`}
      ref={comboboxRef}
      style={{ ...cssCustomProperties, ...styles.root }}
    >
      {label && (
        <label
          className={`combobox__label ${classNames.label}`}
          style={styles.label}
        >
          {label}
        </label>
      )}
      <button
        type="button"
        className={`combobox__trigger ${classNames.trigger}`}
        style={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span
          className={`combobox__selected ${classNames.label}`}
          style={styles.label}
        >
          {selectedLabel}
        </span>
        <div
          className={`combobox__actions ${classNames.actions}`}
          style={styles.actions}
        >
          {value && (
            <button
              type="button"
              className={`combobox__clear ${classNames.clear}`}
              style={styles.clear}
              onClick={handleClear}
            >
              <XIcon />
            </button>
          )}
          <div className={`combobox__chevron ${classNames.chevron}`}>
            <ChevronDownIcon />
          </div>
        </div>
      </button>

      {isOpen && (
        <div
          className={`combobox__menu ${classNames.menu}`}
          style={styles.menu}
        >
          <div
            className={`combobox__search ${classNames.search}`}
            style={styles.search}
          >
            {loading ? <SpinnerIcon /> : <SearchIcon />}
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              autoFocus
              className={`combobox__search-input ${classNames.searchInput}`}
              style={styles.searchInput}
            />
          </div>
          <ul
            className={`combobox__list ${classNames.list}`}
            style={styles.list}
          >
            {options.length > 0 ? (
              options.map((option) => (
                <li
                  key={option[valueKey]}
                  className={`combobox__item ${value && value[valueKey] === option[valueKey] ? 'selected' : ''} ${classNames.item}`}
                  style={styles.item}
                  onClick={() => handleSelectOption(option)}
                >
                  <span>{getOptionLabel(option)}</span>
                  {value && value[valueKey] === option[valueKey] && (
                    <CheckIcon />
                  )}
                </li>
              ))
            ) : (
              <li
                className={`combobox__item--empty ${classNames.emptyItem}`}
                style={styles.emptyItem}
              >
                {loading ? 'Loading...' : 'No results found.'}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Combobox;
